import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IJustifyPositionProps } from 'types/components/components.interface';

export type TableDropdownAlign = 'top' | 'bottom';

export interface TableDropdownPosition {
  top: number;
  left: number;
  align: TableDropdownAlign;
}

interface IUseTableDropdownOptions extends IJustifyPositionProps {
  isOpen: boolean;
  onClose: () => void;
}

const MENU_OFFSET_PX = 4;
const VIEWPORT_PADDING_PX = 8;

const isNestedPopupOpen = () => Boolean(document.querySelector('.popup-overlay'));

const findScrollContainers = (element: HTMLElement | null) => {
  const containers: HTMLElement[] = [];
  let current = element?.parentElement ?? null;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const isScrollable =
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      current.scrollHeight > current.clientHeight;

    if (isScrollable) {
      containers.push(current);
    }

    current = current.parentElement;
  }

  return containers;
};

export const useTableDropdown = ({
  isOpen,
  onClose,
  justifyPosition = 'end',
}: IUseTableDropdownOptions) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<TableDropdownPosition | null>(null);

  const updatePosition = useCallback(() => {
    const triggerElement = triggerRef.current;
    const menuElement = menuRef.current;

    if (!triggerElement || !menuElement) {
      return;
    }

    const triggerRect = triggerElement.getBoundingClientRect();
    const menuHeight = menuElement.offsetHeight;
    const menuWidth = menuElement.offsetWidth;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const align: TableDropdownAlign =
      spaceBelow >= menuHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top';

    let top =
      align === 'bottom'
        ? triggerRect.bottom + MENU_OFFSET_PX
        : triggerRect.top - menuHeight - MENU_OFFSET_PX;

    let left =
      justifyPosition === 'end'
        ? triggerRect.right - menuWidth
        : triggerRect.left;

    left = Math.max(
      VIEWPORT_PADDING_PX,
      Math.min(left, viewportWidth - menuWidth - VIEWPORT_PADDING_PX)
    );
    top = Math.max(
      VIEWPORT_PADDING_PX,
      Math.min(top, viewportHeight - menuHeight - VIEWPORT_PADDING_PX)
    );

    setPosition({ top, left, align });
  }, [justifyPosition]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }

    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleScroll = () => {
      if (isNestedPopupOpen()) {
        return;
      }

      onClose();
    };

    const scrollContainers = findScrollContainers(triggerRef.current);
    scrollContainers.forEach(container => {
      container.addEventListener('scroll', handleScroll, { passive: true });
    });
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      scrollContainers.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
      });
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, onClose, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      if (isNestedPopupOpen()) {
        return;
      }

      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }

      if (isNestedPopupOpen()) {
        return;
      }

      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside, { capture: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, {
        capture: true,
      });
    };
  }, [isOpen, onClose]);

  return {
    triggerRef,
    menuRef,
    position,
    updatePosition,
  } satisfies {
    triggerRef: RefObject<HTMLDivElement>;
    menuRef: RefObject<HTMLDivElement>;
    position: TableDropdownPosition | null;
    updatePosition: () => void;
  };
};
